import { db } from "db/db";
import { Permission } from "db/definitions";

/**
 * Adds a bulk list of permissions to the database.
 * @author Eric Webb <ewebb@factorearth.com>
 * @param perms - An array of `Permission` objects to be added to the database.
 * @returns A promise that resolves when the permissions are successfully added.
 */
export async function bulkAddPerms(perms: Permission[]): Promise<void> {
	await db.permission.bulkAdd(perms);
}

/**
 * Retrieves permissions by their IDs, filtering out any invalid entries.
 * @author Eric Webb <ewebb@factorearth.com>
 * @param ids - Array of permission IDs to retrieve.
 * @returns A promise resolving to an array of valid permissions.
 */
export async function bulkGetPerms(ids: string[]): Promise<Permission[]> {
	const permissions = (await db.permission.bulkGet(ids)).filter((perm): perm is Permission => !!perm);
	return permissions;
}

/**
 * Creates a new permission in the database.
 * @author Eric Webb <ewebb@factorearth.com>
 * @param perm - The permission to be created.
 * @returns A promise that resolves when the permission has been added.
 */
export async function createPermission(perm: Permission): Promise<void> {
	await db.permission.add(perm);
}

/**
 * Deletes all role docs in the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 */
export async function deleteAllPerms(): Promise<void> {
	await db.permission.clear();
}

/**
 * Deletes a permission by its ID from the database.
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id - The ID of the permission to delete.
 * @returns A promise that resolves when the permission is deleted.
 */
export async function deletePerm(id: string): Promise<void> {
	await db.permission.delete(id);
}

/**
 * Fetches all permissions from the database.
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns A promise resolving to an array of all permissions.
 */
export async function fetchPermissions(): Promise<Permission[]> {
	const permissions = await db.permission.toArray();
	return permissions;
}

/**
 * Updates or creates a permission in the database.
 * @author Eric Webb <ewebb@factorearth.com>
 * @param perm - The permission to be added or updated.
 * @returns A promise that resolves when the permission is stored.
 */
export async function putPermission(perm: Permission): Promise<void> {
	await db.permission.put(perm);
}
